import { v4 as uuidv4 } from 'uuid';
import { getDeviceFingerprint } from './deviceFingerprint';
import axios from 'axios';
import { API_URL } from '../config';

/**
 * Activity Tracker Utility
 * Tracks user browsing activity: page views, product views, cart actions, etc.
 */

class ActivityTracker {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.deviceFingerprint = getDeviceFingerprint();
    this.deviceToken = null; // FCM device token for push notifications
    this.currentPageStartTime = Date.now();
    this.activityQueue = [];
    this.isProcessing = false;
    this.BATCH_SIZE = 10;
    this.BATCH_INTERVAL = 5000; // Send batch every 5 seconds
    this.isAdmin = false; // Track if current user is admin

    // Start batch processor
    this.startBatchProcessor();

    // Track when user leaves page
    window.addEventListener('beforeunload', () => this.endSession());
  }

  /**
   * Set device token for linking activities to push notification device
   * @param {string} token - FCM device token
   */
  setDeviceToken(token) {
    this.deviceToken = token;
  }

  /**
   * Set admin status to disable tracking for admin users
   * @param {boolean} isAdmin - Whether current user is admin
   */
  setAdminStatus(isAdmin) {
    this.isAdmin = isAdmin;
  }

  /**
   * Check if tracking should be disabled
   * @returns {boolean} - True if tracking should be skipped
   */
  shouldSkipTracking() {
    // Skip if user is admin
    if (this.isAdmin) {
      return true;
    }

    // Skip if current page is an admin page
    const pathname = window.location.pathname;
    if (pathname.startsWith('/admin')) {
      return true;
    }

    return false;
  }

  /**
   * Get or create session ID from sessionStorage
   * @returns {string} Session ID
   */
  getOrCreateSessionId() {
    const STORAGE_KEY = 'eladore_session_id';
    let sessionId = sessionStorage.getItem(STORAGE_KEY);

    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem(STORAGE_KEY, sessionId);
    }

    return sessionId;
  }

  /**
   * Get UTM parameters from URL
   * @returns {object} UTM parameters
   */
  getUTMParameters() {
    const params = new URLSearchParams(window.location.search);
    return {
      utmSource: params.get('utm_source'),
      utmMedium: params.get('utm_medium'),
      utmCampaign: params.get('utm_campaign')
    };
  }

  /**
   * Track an activity
   * @param {object} activityData - Activity data
   */
  async trackActivity(activityData) {
    // Skip tracking for admin users
    if (this.shouldSkipTracking()) {
      return;
    }

    const activity = {
      sessionId: this.sessionId,
      deviceFingerprint: this.deviceFingerprint,
      deviceToken: this.deviceToken, // Include FCM device token if available
      pageUrl: window.location.href,
      pagePath: window.location.pathname,
      pageTitle: document.title,
      referrer: document.referrer,
      ...this.getUTMParameters(),
      ...activityData
    };

    // Add to queue
    this.activityQueue.push(activity);

    // Send immediately if queue is full
    if (this.activityQueue.length >= this.BATCH_SIZE) {
      await this.sendBatch();
    }
  }

  /**
   * Start batch processor to send activities periodically
   */
  startBatchProcessor() {
    setInterval(() => {
      if (this.activityQueue.length > 0) {
        this.sendBatch();
      }
    }, this.BATCH_INTERVAL);
  }

  /**
   * Send batch of activities to backend
   */
  async sendBatch() {
    if (this.isProcessing || this.activityQueue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const batch = [...this.activityQueue];
    this.activityQueue = [];

    try {
      // Send each activity (in production, you might want to batch these in a single request)
      for (const activity of batch) {
        await axios.post(`${API_URL}/activity/track`, activity);
      }
    } catch (error) {
      console.error('Error sending activity batch:', error);
      // Re-add to queue on failure
      this.activityQueue.unshift(...batch);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Track page view
   * @param {string} pageTitle - Page title
   * @param {string} pagePath - Page path
   */
  async trackPageView(pageTitle = document.title, pagePath = window.location.pathname) {
    // Calculate time on previous page
    const timeOnPage = Math.floor((Date.now() - this.currentPageStartTime) / 1000);

    // Update start time for new page
    this.currentPageStartTime = Date.now();

    await this.trackActivity({
      activityType: 'page_view',
      pageTitle,
      pagePath,
      timeOnPage: timeOnPage > 0 ? timeOnPage : null
    });
  }

  /**
   * Track product view
   * @param {object} product - Product object
   */
  async trackProductView(product) {
    await this.trackActivity({
      activityType: 'product_view',
      productId: product.id,
      productName: product.name,
      productPrice: product.price || product.final_price
    });
  }

  /**
   * Track product click (from list/category page)
   * @param {object} product - Product object
   */
  async trackProductClick(product) {
    await this.trackActivity({
      activityType: 'product_click',
      productId: product.id,
      productName: product.name,
      productPrice: product.price || product.final_price
    });
  }

  /**
   * Track add to cart
   * @param {object} product - Product object
   * @param {number} quantity - Quantity added
   */
  async trackAddToCart(product, quantity = 1) {
    await this.trackActivity({
      activityType: 'add_to_cart',
      productId: product.id,
      productName: product.name,
      productPrice: product.price || product.final_price,
      cartItemQuantity: quantity
    });
  }

  /**
   * Track remove from cart
   * @param {object} product - Product object
   * @param {number} quantity - Quantity removed
   */
  async trackRemoveFromCart(product, quantity = 1) {
    await this.trackActivity({
      activityType: 'remove_from_cart',
      productId: product.id,
      productName: product.name,
      cartItemQuantity: quantity
    });
  }

  /**
   * Track add to wishlist
   * @param {object} product - Product object
   */
  async trackAddToWishlist(product) {
    await this.trackActivity({
      activityType: 'add_to_wishlist',
      productId: product.id,
      productName: product.name,
      productPrice: product.price || product.final_price
    });
  }

  /**
   * Track remove from wishlist
   * @param {object} product - Product object
   */
  async trackRemoveFromWishlist(product) {
    await this.trackActivity({
      activityType: 'remove_from_wishlist',
      productId: product.id,
      productName: product.name
    });
  }

  /**
   * Track search
   * @param {string} query - Search query
   * @param {number} resultsCount - Number of results
   */
  async trackSearch(query, resultsCount = 0) {
    await this.trackActivity({
      activityType: 'search',
      searchQuery: query,
      searchResultsCount: resultsCount
    });
  }

  /**
   * Track checkout start
   */
  async trackCheckoutStart() {
    await this.trackActivity({
      activityType: 'checkout_start'
    });
  }

  /**
   * Track order complete
   * @param {object} order - Order object
   */
  async trackOrderComplete(order) {
    await this.trackActivity({
      activityType: 'order_complete',
      orderId: order.id,
      orderTotal: order.final_price || order.total
    });
  }

  /**
   * Track scroll depth
   * @param {number} depth - Scroll depth percentage (0-100)
   */
  async trackScrollDepth(depth) {
    // Only track at certain milestones: 25%, 50%, 75%, 100%
    const milestones = [25, 50, 75, 100];
    const milestone = milestones.find(m => depth >= m && depth < m + 5);

    if (milestone && !this[`scrollMilestone${milestone}`]) {
      this[`scrollMilestone${milestone}`] = true;

      await this.trackActivity({
        activityType: 'page_view', // Update the existing page view
        scrollDepth: milestone,
        timeOnPage: Math.floor((Date.now() - this.currentPageStartTime) / 1000)
      });
    }
  }

  /**
   * End session
   */
  async endSession() {
    // Send any remaining activities
    if (this.activityQueue.length > 0) {
      await this.sendBatch();
    }

    // Send end session request
    try {
      await axios.post(`${API_URL}/activity/end-session`, {
        sessionId: this.sessionId
      });
    } catch (error) {
      console.error('Error ending session:', error);
    }
  }

  /**
   * Reset scroll milestones when navigating to new page
   */
  resetScrollTracking() {
    this.scrollMilestone25 = false;
    this.scrollMilestone50 = false;
    this.scrollMilestone75 = false;
    this.scrollMilestone100 = false;
  }
}

// Export singleton instance
const activityTracker = new ActivityTracker();

// Also track scroll depth
let scrollTrackingInitialized = false;

export function initScrollTracking() {
  if (scrollTrackingInitialized) return;
  scrollTrackingInitialized = true;

  let ticking = false;

  const trackScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const scrollPercent = (scrollTop / docHeight) * 100;

    activityTracker.trackScrollDepth(scrollPercent);
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(trackScroll);
      ticking = true;
    }
  });
}

export default activityTracker;
